// The league standings as plain text, for the share sheet: who is where, and
// on how many points. Deliberately nothing else. The table it is shared from
// carries six columns, and a chat bubble has room for a name and a number.
import { SHARE_SITE } from "./lineupShare";

const MEDALS = ["🥇", "🥈", "🥉"];
const PLAYOFF_LINE = "➖ Playoff line ➖";
const pts = (n) => (Math.abs(n) === 1 ? "pt" : "pts");

/**
 * What to call each manager. First name, since that is what a group chat
 * knows them by. A name shared by two managers gets the squad name after it
 * so the two rows are not identical, and a manager with no usable first name
 * is called by their squad.
 *
 * "Shared by two managers" counts people, not rows: a record list can hold
 * the same manager several times (their week 2 and their week 4), and that
 * is one Dana, not two.
 */
export function displayNames(rows) {
  const holders = {};
  rows.forEach((r) => {
    const key = (r.firstName || "").trim().toLowerCase();
    if (key) (holders[key] = holders[key] || new Set()).add(r.memberId ?? r.teamName);
  });
  return rows.map((r) => {
    const first = (r.firstName || "").trim();
    if (!first || first === "Unknown") return r.teamName;
    return holders[first.toLowerCase()].size > 1 ? `${first} (${r.teamName})` : first;
  });
}

/**
 * @param {object} standings
 * @param {string} standings.leagueName
 * @param {string} [standings.weekLabel] "Week 5", "After Week 3". Omitted in
 *   the preseason, when there is no week to name.
 * @param {{ rank: number, firstName: string, teamName: string, points: number }[]} standings.rows
 *   in any order; sorted here by rank
 * @param {number} [standings.playoffSpots] how many places make the playoffs.
 *   A line is drawn under the last of them, as on the table. Left out, or
 *   when everyone listed is inside it, no line.
 */
export function buildStandingsShareText({ leagueName, weekLabel, rows = [], playoffSpots }) {
  const ordered = [...rows].sort((a, b) => a.rank - b.rank);
  const names = displayNames(ordered);

  const lines = [`🏆 ${leagueName}${weekLabel ? ` · ${weekLabel}` : ""}`, ""];
  if (ordered.length === 0) lines.push("No managers yet.");
  ordered.forEach((row, i) => {
    const place = MEDALS[row.rank - 1] || `${row.rank}.`;
    const points = row.points ?? 0;
    lines.push(`${place} ${names[i]} ${points} ${pts(points)}`);
    // By position, not by rank number, so a shared rank at the cut cannot
    // draw the line twice or skip it.
    if (playoffSpots && i === playoffSpots - 1 && ordered.length > playoffSpots) lines.push(PLAYOFF_LINE);
  });
  lines.push("", SHARE_SITE);

  return lines.join("\n");
}
